/**
 * AgentOperationsPanel — despliegue y histórico del agente (estándar de listados).
 *
 * QUÉ HABÍA ANTES
 * ---------------------------------------------------------------------------
 * Dos tarjetas de Bootstrap con `card border-0 shadow-sm`, tarjetitas grises,
 * `table table-sm` en claro, `<select>` y `<input>` de Bootstrap, dos bloques
 * `<pre class="bg-dark text-light">` con fondo oscuro a mano (ilegibles en los
 * skins de cristal) y **todo el texto de despliegue dentro de la pantalla**:
 * cuatro comandos, los artefactos publicados y un runbook de cuatro puntos. Es
 * lo que hacía que la sección pareciera un cuadro de mando antiguo.
 *
 * AHORA
 * ---------------------------------------------------------------------------
 * Lo **operativo** (comandos, artefactos y runbook) sale de la vista y pasa a un
 * diálogo «Comandos de despliegue»: se consulta cuando se despliega, no ocupa
 * sitio siempre. La pantalla queda para lo que sí se mira a diario — el
 * **histórico de snapshots** — como listado estándar **embebido**: buscador,
 * filtros, columnas elegibles, Lista/Fichas y export, con el detalle (diff y
 * payload) en el diálogo común.
 *
 * El histórico se pide al backend con el límite elegido (los snapshots llevan
 * el JSON completo y traerlos todos sería traer megas) y el resto de filtros se
 * aplican en cliente, que es la regla del estándar.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SignalIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ArrowPathIcon,
  CommandLineIcon,
  ClipboardDocumentIcon,
  InformationCircleIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import { CommandBlock } from './common/Panel';
import { docElementPath } from '../utils/docElements';
import { hace } from '../utils/relativeTime';
import api from '../services/api';

const CAMBIOS = {
  created: { label: 'Creado', tono: 'list-badge--on' },
  updated: { label: 'Actualizado', tono: 'list-badge--warn' },
  unchanged: { label: 'Sin cambios', tono: 'list-badge--muted' },
};

const TRIGGERS = {
  daily_rescan: 'Rescan diario',
  remote_command: 'Comando remoto',
  manual: 'Manual',
};

const CI_TIPOS = [
  { value: 'rack_element', label: 'Servidor' },
  { value: 'workstation', label: 'Estación de trabajo' },
  { value: 'virt_hypervisor', label: 'Hipervisor' },
  { value: 'virt_vm', label: 'Máquina virtual' },
];

const LIMITES = [20, 40, 100, 200];

function formatearJson(valor) {
  try {
    return JSON.stringify(valor || {}, null, 2);
  } catch {
    return String(valor);
  }
}

function urlApiCmdb() {
  const configurada = (process.env.REACT_APP_API_URL || '').trim();
  if (configurada) {
    if (/^https?:\/\//i.test(configurada)) return configurada.replace(/\/$/, '');
    if (typeof window !== 'undefined') {
      const normalizada = configurada.startsWith('/') ? configurada : `/${configurada}`;
      return `${window.location.origin}${normalizada}`.replace(/\/$/, '');
    }
    return configurada.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined') return `${window.location.origin}/api`;
  return '/api';
}

/** Bloque de comando: monoespaciado, con botón de copiar. */


export default function AgentOperationsPanel({ companyId }) {
  const navigate = useNavigate();

  const [policy, setPolicy] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [events, setEvents] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [installerInfo, setInstallerInfo] = useState(null);
  const [packageInfo, setPackageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);

  const [limit, setLimit] = useState(40);
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  // Purga del histórico (no toca ningún equipo: solo el registro de lo que hizo el agente)
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeCounts, setPurgeCounts] = useState(null);
  const [purgeScopes, setPurgeScopes] = useState({ snapshots: true, commands: true, events: false });
  const [purging, setPurging] = useState(false);

  const [search, setSearch] = useState('');
  const [filterCiType, setFilterCiType] = useState('');
  const [filterTrigger, setFilterTrigger] = useState('');
  const [filterChange, setFilterChange] = useState('');
  const [filterAgent, setFilterAgent] = useState('');
  const [groupMode, setGroupMode] = useState('');

  const cargarMetadatos = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [policyRes, tokensRes, credentialsRes, eventsRes, companiesRes, installerRes, packageRes] =
        await Promise.all([
          api.getCompanyAgentPolicy(companyId),
          api.getAgentBootstrapTokens(companyId),
          api.getAgentCredentials(companyId),
          api.getAgentCredentialEvents(companyId),
          api.getAuthorizedCompanies(),
          api.getAgentInstallerInfo().catch(() => ({ installer: null })),
          api.getAgentPackageInfo().catch(() => ({ package: null })),
        ]);
      setPolicy(policyRes.policy || null);
      setTokens(tokensRes.tokens || []);
      setCredentials(credentialsRes.credentials || []);
      setEvents(eventsRes.events || []);
      setCompanies(companiesRes || []);
      setInstallerInfo(installerRes.installer || null);
      setPackageInfo(packageRes.package || null);
    } catch (e) {
      setError(e.message || 'No se pudo cargar la operación del agente');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const cargarSnapshots = useCallback(async () => {
    if (!companyId) return;
    setSnapshotsLoading(true);
    setError('');
    try {
      const res = await api.getAgentInventorySnapshots(companyId, { limit });
      setSnapshots(res.snapshots || []);
    } catch (e) {
      setError(e.message || 'No se pudo cargar el histórico de snapshots');
      setSnapshots([]);
    } finally {
      setSnapshotsLoading(false);
    }
  }, [companyId, limit]);

  useEffect(() => { cargarMetadatos(); }, [cargarMetadatos]);
  useEffect(() => { cargarSnapshots(); }, [cargarSnapshots]);

  const mostrarAviso = (texto) => {
    setAviso(texto);
    setTimeout(() => setAviso(''), 2500);
  };

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      mostrarAviso('Copiado al portapapeles');
    } catch {
      setError('El navegador no dejó copiar al portapapeles');
    }
  };

  /** Abre el diálogo de purga contando antes lo que hay (para poder avisar). */
  const abrirPurga = async () => {
    setError('');
    try {
      const res = await api.previewAgentHistoryPurge(companyId);
      setPurgeCounts(res);
    } catch (e) {
      setError(e.message || 'No se pudo contar el histórico');
      return;
    }
    setPurgeOpen(true);
  };

  const purgar = async () => {
    setPurging(true);
    setError('');
    try {
      const res = await api.purgeAgentHistory(companyId, purgeScopes);
      const d = res?.deleted || {};
      setPurgeOpen(false);
      mostrarAviso(
        `Borrado: ${d.snapshots || 0} snapshots · ${d.commands || 0} comandos · ${d.events || 0} eventos`,
      );
      await Promise.all([cargarMetadatos(), cargarSnapshots()]);
    } catch (e) {
      setError(e.message || 'No se pudo purgar el histórico');
    } finally {
      setPurging(false);
    }
  };

  const empresa = useMemo(
    () => companies.find((c) => String(c.id) === String(companyId)) || null,
    [companies, companyId],
  );
  const tokenActivo = useMemo(() => tokens.find((t) => t.is_active) || null, [tokens]);
  const credencialesActivas = useMemo(() => credentials.filter((c) => c.is_active), [credentials]);
  const ultimoEvento = events[0] || null;

  const slug = empresa?.slug || '<COMPANY_SLUG>';
  const apiUrl = useMemo(() => urlApiCmdb(), []);
  const instalador = installerInfo?.filename || 'cmdb-agent-installer.exe';
  const paquete = packageInfo?.filename || 'cmdb-agent-package.zip';

  const comandos = useMemo(() => ([
    {
      titulo: 'Instalación silenciosa con bootstrap',
      descripcion: 'Despliegue por empresa sin compartir una API key. El instalador enrola en el primer arranque.',
      comando: `${instalador} /S /CMDB_URL=${apiUrl} /COMPANY_SLUG=${slug} /BOOTSTRAP_TOKEN=<BOOTSTRAP_TOKEN_RECIEN_EMITIDO> /AUTO_START_SERVICE=1`,
    },
    {
      titulo: 'Fallback silencioso con API key de empresa',
      descripcion: 'Solo si la empresa sigue en modo legacy o durante una convivencia controlada.',
      comando: `${instalador} /S /CMDB_URL=${apiUrl} /CMDB_API_KEY=<API_KEY_EMPRESA> /AUTO_START_SERVICE=1`,
    },
    {
      titulo: 'Bootstrap manual por CLI',
      descripcion: 'Para revisar un equipo ya instalado o resolver un alta que quedó con el .env incompleto.',
      comando: `python agent.py bootstrap --url ${apiUrl} --company-slug ${slug} --bootstrap-token <BOOTSTRAP_TOKEN_RECIEN_EMITIDO>`,
    },
    {
      titulo: 'Actualización en sitio',
      descripcion: 'Refresca los binarios sin reinstalar ni tocar el .env ni el .agent_id.',
      comando: `python agent.py update --url ${apiUrl} --key <API_KEY_O_CREDENCIAL_AGENTE>`,
    },
  ]), [apiUrl, instalador, slug]);

  /* ── filtrado ───────────────────────────────────────────────────────────── */
  const etiquetaCi = (tipo) => CI_TIPOS.find((t) => t.value === tipo)?.label || tipo || '—';
  const nombreCi = (s) => s.snapshot?.ci?.name || s.snapshot?.ci?.hostname || 'Sin nombre';

  const agentes = useMemo(() => {
    const ids = [...new Set(snapshots.map((s) => s.agent_id).filter(Boolean))];
    return ids.sort().map((id) => ({ value: id, label: `${id.slice(0, 8)}…${id.slice(-6)}` }));
  }, [snapshots]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return snapshots.filter((s) => {
      if (filterCiType && s.ci_type !== filterCiType) return false;
      if (filterTrigger && s.trigger !== filterTrigger) return false;
      if (filterChange && s.change_status !== filterChange) return false;
      if (filterAgent && s.agent_id !== filterAgent) return false;
      if (!term) return true;
      return [
        nombreCi(s), s.ci_type, s.agent_id,
        (CAMBIOS[s.change_status] || {}).label, TRIGGERS[s.trigger],
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
    });
  }, [snapshots, search, filterCiType, filterTrigger, filterChange, filterAgent]);

  const clearFilters = () => {
    setSearch(''); setFilterCiType(''); setFilterTrigger(''); setFilterChange(''); setFilterAgent('');
  };

  const cuenta = useMemo(() => ({
    total: snapshots.length,
    creados: snapshots.filter((s) => s.change_status === 'created').length,
    actualizados: snapshots.filter((s) => s.change_status === 'updated').length,
    sinCambios: snapshots.filter((s) => s.change_status === 'unchanged').length,
  }), [snapshots]);

  const kpis = (
    <>
      <div className="list-kpis list-kpis--chips">
        <button type="button" className="list-kpi-chip" onClick={clearFilters} title="Ver todos los snapshots cargados">
          <span className="list-kpi-value">{cuenta.total}</span>
          <span className="list-kpi-label">snapshots</span>
        </button>
        <button type="button" className="list-kpi-chip"
          onClick={() => { clearFilters(); setFilterChange('created'); }} title="Ver los que crearon inventario">
          <span className="list-kpi-value">{cuenta.creados}</span>
          <span className="list-kpi-label">creados</span>
        </button>
        <button type="button" className="list-kpi-chip"
          onClick={() => { clearFilters(); setFilterChange('updated'); }} title="Ver los que trajeron cambios">
          <span className="list-kpi-value">{cuenta.actualizados}</span>
          <span className="list-kpi-label">actualizados</span>
        </button>
        <button type="button" className="list-kpi-chip"
          onClick={() => { clearFilters(); setFilterChange('unchanged'); }} title="Ver los que no cambiaron nada">
          <span className="list-kpi-value">{cuenta.sinCambios}</span>
          <span className="list-kpi-label">sin cambios</span>
        </button>
      </div>
      <div className="list-kpis list-kpis--chips" style={{ marginTop: 8 }}>
        <span className="list-kpi-chip" title="Modo de despliegue de la empresa">
          <span className="list-kpi-value">{policy?.rollout_mode || 'legacy'}</span>
          <span className="list-kpi-label">modo</span>
        </span>
        <span className="list-kpi-chip" title={tokenActivo?.token_prefix ? `Token ${tokenActivo.token_prefix}…` : 'Sin token activo'}>
          <span className="list-kpi-value">{tokenActivo ? 'Sí' : 'No'}</span>
          <span className="list-kpi-label">bootstrap activo</span>
        </span>
        <span className="list-kpi-chip" title={`${credentials.length} credenciales registradas en total`}>
          <span className="list-kpi-value">{credencialesActivas.length}</span>
          <span className="list-kpi-label">credenciales activas</span>
        </span>
        <span className="list-kpi-chip" title={ultimoEvento ? new Date(ultimoEvento.created_at).toLocaleString() : 'Sin actividad registrada'}>
          <span className="list-kpi-value">{ultimoEvento?.event_type || '—'}</span>
          <span className="list-kpi-label">último evento</span>
        </span>
      </div>
    </>
  );

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'created_at',
        label: 'Fecha',
        sortable: true,
        render: (s) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span>{new Date(s.created_at).toLocaleString()}</span>
            <span className="list-hint">{hace(s.created_at)}</span>
          </span>
        ),
      },
      {
        key: 'change_status',
        label: 'Cambio',
        sortable: true,
        sortValue: (s) => (CAMBIOS[s.change_status] || {}).label || s.change_status,
        render: (s) => {
          const cambio = CAMBIOS[s.change_status] || { label: s.change_status, tono: 'list-badge--muted' };
          return <span className={`list-badge ${cambio.tono}`}>{cambio.label}</span>;
        },
      },
      {
        key: 'trigger',
        label: 'Origen',
        sortable: true,
        sortValue: (s) => TRIGGERS[s.trigger] || s.trigger,
        render: (s) => <span className="list-badge">{TRIGGERS[s.trigger] || s.trigger || '—'}</span>,
      },
      {
        key: 'ci',
        label: 'Dispositivo',
        sortable: true,
        sortValue: (s) => nombreCi(s),
        render: (s) => {
          if (!s.ci_type) return dash;
          const ruta = docElementPath(s.ci_type, s.ci_id);
          return (
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <button
                type="button"
                className="list-badge"
                style={{ cursor: ruta ? 'pointer' : 'default', alignSelf: 'flex-start' }}
                onClick={(event) => {
                  event.stopPropagation();
                  if (ruta) { navigate(ruta); return; }
                  setSelected(s);
                }}
                title={ruta ? 'Ver el dispositivo' : 'Este tipo no tiene pantalla propia'}
              >
                {nombreCi(s)}
              </button>
              <span className="list-hint">{etiquetaCi(s.ci_type)} #{s.ci_id}</span>
            </span>
          );
        },
      },
      {
        key: 'agent_id',
        label: 'Agente',
        sortable: true,
        render: (s) => (s.agent_id
          ? <span className="list-code" title={s.agent_id}>{s.agent_id.slice(0, 8)}…</span>
          : dash),
      },
      {
        key: 'command_id',
        label: 'Comando',
        sortable: true,
        render: (s) => (s.command_id ? <span className="list-code">#{s.command_id}</span> : dash),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const filters = [
    {
      key: 'change', label: 'Cambio', allLabel: 'Cualquier cambio',
      value: filterChange, onChange: setFilterChange,
      options: Object.entries(CAMBIOS).map(([value, c]) => ({ value, label: c.label })),
    },
    {
      key: 'trigger', label: 'Origen', allLabel: 'Cualquier origen',
      value: filterTrigger, onChange: setFilterTrigger,
      options: Object.entries(TRIGGERS).map(([value, label]) => ({ value, label })),
    },
    {
      key: 'citype', label: 'Tipo de dispositivo', allLabel: 'Cualquier tipo',
      value: filterCiType, onChange: setFilterCiType,
      options: CI_TIPOS,
    },
    {
      key: 'agent', label: 'Agente', allLabel: 'Cualquier agente',
      value: filterAgent, onChange: setFilterAgent,
      options: agentes,
    },
    {
      key: 'group', label: 'Agrupar por', allLabel: 'Sin agrupar',
      value: groupMode, onChange: setGroupMode,
      options: [
        { value: 'change', label: 'Cambio' },
        { value: 'trigger', label: 'Origen' },
        { value: 'citype', label: 'Tipo de dispositivo' },
      ],
    },
  ];

  const groupBy = useMemo(() => {
    if (groupMode === 'change') return (s) => (CAMBIOS[s.change_status] || {}).label || s.change_status;
    if (groupMode === 'trigger') return (s) => TRIGGERS[s.trigger] || s.trigger || 'Sin origen';
    if (groupMode === 'citype') return (s) => etiquetaCi(s.ci_type);
    return undefined;
  }, [groupMode]);

  const exportRows = useMemo(() => filtered.map((s) => ({
    'Fecha': s.created_at ? new Date(s.created_at).toLocaleString() : '',
    'Cambio': (CAMBIOS[s.change_status] || {}).label || s.change_status || '',
    'Origen': TRIGGERS[s.trigger] || s.trigger || '',
    'Dispositivo': nombreCi(s),
    'Tipo': etiquetaCi(s.ci_type),
    'CI ID': s.ci_id || '',
    'Agente': s.agent_id || '',
    'Comando': s.command_id || '',
  })), [filtered]);

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1rem 0' }}>
        <div className="list-hint">Selecciona una empresa para revisar el despliegue y el histórico del agente.</div>
      </div>
    );
  }

  return (
    <>
      <ListPage
        icon={SignalIcon}
        title="Operación del agente"
        subtitle={`Últimos ${limit} snapshots · ${filtered.length} filtrados`}
        embedded

        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por dispositivo, agente u origen…"
        filters={filters}

        exportRows={exportRows}
        exportFileBase="snapshots-agente"
        exportSheetName="Snapshots"

        columns={columns}
        columnStorageKey="cmdb_cols_agent_snapshots"
        defaultVisible={['created_at', 'change_status', 'trigger', 'ci', 'agent_id']}
        rows={filtered}
        rowKey={(s) => s.id}
        rowTitle={(s) => `Ver el snapshot de ${nombreCi(s)}`}
        onRowClick={(s) => setSelected(s)}
        groupBy={groupBy}

        kpis={kpis}

        headerActions={(
          <>
            {aviso && <span className="list-badge list-badge--on">{aviso}</span>}
            <label className="list-hint" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Ventana
              <select
                className="list-select"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                title="Cuántos snapshots recientes se piden al backend"
              >
                {LIMITES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button type="button" className="list-btn-secondary" onClick={cargarSnapshots} disabled={snapshotsLoading}>
              <ArrowPathIcon style={{ width: 15, height: 15 }} /> {snapshotsLoading ? 'Cargando…' : 'Actualizar'}
            </button>
            <button type="button" className="list-btn-secondary" onClick={() => setHelpOpen(true)}>
              <CommandLineIcon style={{ width: 15, height: 15 }} /> Comandos de despliegue
            </button>
            <button type="button" className="list-btn-danger" onClick={abrirPurga}>
              <TrashIcon style={{ width: 15, height: 15 }} /> Purgar operaciones
            </button>
          </>
        )}

        views={[
          { key: 'list', label: 'Lista', icon: ListBulletIcon, title: 'Tabla con todas las columnas' },
          {
            key: 'card', label: 'Fichas', icon: Squares2X2Icon,
            title: 'Tarjetas con lo esencial de cada snapshot',
            render: ({ rows, rowActions }) => (
              <div className="list-cards">
                {rows.map((s) => {
                  const cambio = CAMBIOS[s.change_status] || { label: s.change_status, tono: 'list-badge--muted' };
                  return (
                    <div key={s.id} className="list-card">
                      <div className="list-card-head">
                        <div>
                          <div className="list-card-title">{nombreCi(s)}</div>
                          <span className="list-hint">{hace(s.created_at)}</span>
                        </div>
                        <span className={`list-badge ${cambio.tono}`}>{cambio.label}</span>
                      </div>
                      <div className="list-card-meta">
                        <span className="list-badge">{TRIGGERS[s.trigger] || s.trigger || '—'}</span>
                        <span className="list-hint">{etiquetaCi(s.ci_type)} #{s.ci_id}</span>
                        {s.command_id && <span className="list-code">#{s.command_id}</span>}
                      </div>
                      <div className="list-card-actions">{rowActions(s)}</div>
                    </div>
                  );
                })}
              </div>
            ),
          },
        ]}
        listKey="agent_snapshots"
        defaultView="list"

        loading={loading && snapshots.length === 0}
        loadingText="Cargando operación del agente…"
        error={error}

        empty={{
          icon: '📈',
          title: snapshots.length === 0
            ? 'Todavía no hay snapshots de inventario'
            : 'Ningún snapshot coincide con los filtros',
          subtitle: snapshots.length === 0
            ? 'El agente guarda un snapshot en cada rescan diario, comando remoto o alta por bootstrap.'
            : undefined,
        }}
      />

      {/* ══ Purga del histórico ══ */}
      {purgeOpen && (
        <Dialog
          title="Purgar las operaciones del agente"
          icon={<TrashIcon style={{ width: 18, height: 18 }} />}
          maxWidth={620}
          busy={purging}
          onClose={() => setPurgeOpen(false)}
          footer={(
            <>
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => setPurgeOpen(false)}
                disabled={purging}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="list-btn-danger"
                onClick={purgar}
                disabled={purging || !(purgeScopes.snapshots || purgeScopes.commands || purgeScopes.events)}
              >
                {purging ? 'Borrando…' : 'Borrar lo seleccionado'}
              </button>
            </>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            Esto borra el <strong>histórico del agente</strong> de esta empresa: el registro de lo que
            envió y recibió. No borra ningún equipo, ni discos, ni credenciales, así que{' '}
            <strong>no quita duplicados</strong>: solo deja el historial en blanco.
          </p>

          <div
            className="list-badge list-badge--warn"
            style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 12px', marginBottom: 12, fontSize: '0.78rem' }}
          >
            <ExclamationTriangleIcon style={{ width: 15, height: 15, flexShrink: 0, marginTop: 2 }} />
            <span>
              Si el problema es que un agente sigue tocando equipos que no le tocan, esto <strong>no lo
              detiene</strong>: hay que revocar y borrar su credencial en la pestaña Credenciales.
            </span>
          </div>

          {[
            { key: 'snapshots', label: 'Historial de snapshots', que: 'lo que lista esta pantalla' },
            { key: 'commands', label: 'Comandos (encolados y ejecutados)', que: 'la cola de la pestaña Comandos' },
            { key: 'events', label: 'Eventos de credenciales', que: 'el rastro de seguridad: revocaciones y reemisiones' },
          ].map((opcion) => {
            const total = purgeCounts ? purgeCounts[opcion.key] : '·';
            return (
              <label
                key={opcion.key}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '8px 10px', marginBottom: 6,
                  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-toolbar)', cursor: 'pointer',
                  opacity: total === 0 ? 0.6 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={purgeScopes[opcion.key]}
                  onChange={(e) => setPurgeScopes((p) => ({ ...p, [opcion.key]: e.target.checked }))}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <span style={{ fontWeight: 'var(--fw-semibold)' }}>{opcion.label}</span>{' '}
                  <span className="list-code">{total}</span>
                  <br />
                  <span className="list-hint">{opcion.que}</span>
                </span>
              </label>
            );
          })}

          <p className="list-hint" style={{ margin: '10px 0 0' }}>
            Es definitivo y no se puede deshacer. Si lo que quieres es guardar la prueba de lo que pasó,
            abre antes el detalle de un snapshot y copia el JSON.
          </p>
        </Dialog>
      )}

      {/* ══ Detalle del snapshot ══ */}
      {selected && (
        <Dialog
          title={`Snapshot · ${nombreCi(selected)}`}
          icon={<SignalIcon style={{ width: 18, height: 18 }} />}
          maxWidth={820}
          onClose={() => setSelected(null)}
          footer={(
            <>
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => copiar(formatearJson(selected))}
              >
                <ClipboardDocumentIcon style={{ width: 14, height: 14 }} /> Copiar el JSON
              </button>
              <button type="button" className="list-btn-secondary" onClick={() => setSelected(null)}>
                Cerrar
              </button>
            </>
          )}
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <span className={`list-badge ${(CAMBIOS[selected.change_status] || {}).tono || 'list-badge--muted'}`}>
              {(CAMBIOS[selected.change_status] || {}).label || selected.change_status}
            </span>
            <span className="list-badge">{TRIGGERS[selected.trigger] || selected.trigger || '—'}</span>
            <span className="list-hint">{new Date(selected.created_at).toLocaleString()}</span>
          </div>
          <div className="list-hint" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <span>CI: <strong>{etiquetaCi(selected.ci_type)} #{selected.ci_id}</strong></span>
            {selected.agent_id && <span className="list-code" title={selected.agent_id}>{selected.agent_id.slice(0, 8)}…</span>}
            {selected.command_id && <span className="list-code">comando #{selected.command_id}</span>}
          </div>

          <div style={{ fontWeight: 'var(--fw-semibold)', fontSize: '0.82rem', marginBottom: 4 }}>Diff aplicado</div>
          <pre
            style={{
              margin: '0 0 14px', maxHeight: 200, overflow: 'auto', padding: '10px 12px',
              borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
              background: 'var(--bg-surface)', color: 'var(--text-primary)',
              fontFamily: 'var(--font-mono)', fontSize: '0.75rem', whiteSpace: 'pre-wrap',
            }}
          >
            {formatearJson(selected.diff)}
          </pre>

          <div style={{ fontWeight: 'var(--fw-semibold)', fontSize: '0.82rem', marginBottom: 4 }}>Inventario persistido</div>
          <pre
            style={{
              margin: 0, maxHeight: 280, overflow: 'auto', padding: '10px 12px',
              borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
              background: 'var(--bg-surface)', color: 'var(--text-primary)',
              fontFamily: 'var(--font-mono)', fontSize: '0.75rem', whiteSpace: 'pre-wrap',
            }}
          >
            {formatearJson(selected.snapshot)}
          </pre>
        </Dialog>
      )}

      {/* ══ Comandos de despliegue (antes ocupaban media pantalla) ══ */}
      {helpOpen && (
        <Dialog
          title="Comandos de despliegue"
          icon={<CommandLineIcon style={{ width: 18, height: 18 }} />}
          maxWidth={780}
          onClose={() => setHelpOpen(false)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setHelpOpen(false)}>
              Cerrar
            </button>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            Empresa <strong>{empresa?.name || `Company ${companyId}`}</strong> · slug
            <span className="list-code" style={{ marginLeft: 6 }}>{slug}</span> · URL del API
            <span className="list-code" style={{ marginLeft: 6 }}>{apiUrl}</span>
          </p>

          {comandos.map((c) => (
            <CommandBlock
              key={c.titulo}
              title={c.titulo}
              hint={c.descripcion}
              command={c.comando}
            />
          ))}

          <div
            style={{
              borderTop: '1px solid var(--border)',
              paddingTop: 10,
              marginTop: 4,
            }}
          >
            <div style={{ fontWeight: 'var(--fw-semibold)', fontSize: '0.82rem', marginBottom: 6 }}>
              <InformationCircleIcon style={{ width: 14, height: 14, verticalAlign: '-2px', marginRight: 4 }} />
              Runbook rápido
            </div>
            <ul className="list-hint" style={{ margin: 0, paddingLeft: 18 }}>
              <li>Si falla el bootstrap, comprueba primero que la empresa no siga en modo <strong>legacy</strong>.</li>
              <li>Si el servicio instala pero no enrola, revisa <code>.env</code>: <code>CMDB_URL</code>, <code>CMDB_COMPANY_SLUG</code> y <code>CMDB_BOOTSTRAP_TOKEN</code>.</li>
              <li>Si un equipo queda desalineado, reemite o revoca su credencial individual en la pestaña Bootstrap antes de reinstalar.</li>
              <li>Si el agente está online pero el inventario no converge, lanza un rescan y mira el último snapshot con <strong>Actualizado</strong>.</li>
            </ul>
          </div>

          <div
            style={{
              display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12,
              paddingTop: 10, borderTop: '1px solid var(--border)',
            }}
          >
            <span className="list-badge" title={`Nombre que espera el backend: ${instalador}`}>
              EXE {installerInfo ? `${installerInfo.filename} · ${installerInfo.version || 'sin versión'}` : 'sin publicar'}
            </span>
            <span className="list-badge" title={`Nombre que espera el backend: ${paquete}`}>
              ZIP {packageInfo ? `${packageInfo.filename} · ${packageInfo.version || 'sin versión'}` : 'sin publicar'}
            </span>
          </div>
        </Dialog>
      )}
    </>
  );
}
